import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { BookingServicesCategory } from '@views/common/models/booking-services.enum';
import { BoxLevelEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/box';
import { IconSizeEnum, IconTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/icon';
import { ListSizeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/list';
import { Service, ServicesPerPassenger } from 'app/views/booking-detail/models/booking-details.model';
import { BookingService, BookingServicesViewModel } from './models/booking-services.model';

@Component({
  selector: 'app-booking-services',
  templateUrl: './booking-services.component.html',
  styleUrl: './booking-services.component.scss'
})
export class BookingServicesComponent implements OnInit {
  @Input({ required: true })
  public bookingServices: ServicesPerPassenger[] = [];

  public bookingServicesViewModel: BookingServicesViewModel[] = [];

  public boxLevelEnum = BoxLevelEnum;
  public listSizeEnum = ListSizeEnum;
  public iconTypeEnum = IconTypeEnum;
  public iconSizeEnum = IconSizeEnum;
  public bookingServiceCategory = BookingServicesCategory;

  private readonly _translatePipe: TranslatePipe = inject(TranslatePipe);

  public ngOnInit(): void {
    this.bookingServicesViewModel = this.parseBookingServices();
  }

  private parseSeats(seats: (string | undefined)[] | undefined): string {
    if (!seats || seats.every((seat) => !seat)) {
      return 'booking_details_component.booking_services_component.zero_amount_service_categories.seat';
    }
    let acc = '';
    seats.forEach((seat, i) => {
      acc = !seat ? acc.concat('-') : acc.concat(seat);
      if (seats[i + 1] !== undefined) {
        acc = acc.concat('/');
      }
    });
    return acc;
  }
  private parseGcbg(amount: number): string {
    if (amount === 0) {
      return 'booking_details_component.booking_services_component.zero_amount_service_categories.gcbg';
    }
    if (amount === 1) {
      return 'booking_details_component.booking_services_component.service_categories.passenger_services.singular.gcbg';
    }
    return 'booking_details_component.booking_services_component.service_categories.passenger_services.plural.gcbg';
  }

  public showCheck(label: string, amount: number): boolean {
    return label === 'HBAG' || amount > 0;
  }
  public setLabel(label: string, amount: number, seats: (string | undefined)[] | undefined): string {
    if (label === 'GCBG' || label === 'NGCB') {
      return this.parseGcbg(amount);
    }

    if (label === 'EXST' && seats) {
      const parsedSeats = seats.flatMap((seat) => seat?.split('-')[1]);
      return `${this._translatePipe.transform(this.bookingServiceCategory[label]?.singular)} (${parsedSeats.join(' - ')})`;
    }

    if (amount === 1) {
      return this.bookingServiceCategory[label]?.singular ?? 'undefined';
    }
    return this.bookingServiceCategory[label].plural!;
  }

  private organizeServices(services: Service[]): Service[] {
    const order = ['GCBG', 'NGCB', 'HBAG'];
    return services.sort((a, b) => {
      return order.indexOf(a.serviceName) - order.indexOf(b.serviceName);
    });
  }

  private parseServices(
    services: Service[] | undefined,
    seats: (string | undefined)[] | undefined
  ): BookingService[] | undefined {
    const organizedServices =
      this.organizeServices(services!)
        .reverse()
        .flatMap((service) => {
          return {
            count: service.count,
            label: this.setLabel(service.serviceName, service.count, seats),
            icon: this.bookingServiceCategory[service.serviceName]?.icon ?? 'undefined'
          };
        }) ?? [];
    const seatServices = {
      count: seats?.filter((seat) => !!seat).length ?? 0,
      label: this.parseSeats(seats),
      icon: this.bookingServiceCategory['SEAT'].icon
    };

    organizedServices.splice(2, 0, seatServices);

    return organizedServices;
  }

  private parseBookingServices(): BookingServicesViewModel[] {
    return this.bookingServices.flatMap((servicesPerPax) => {
      return {
        hasInfant: servicesPerPax.hasInfant ?? false,
        servicesOutbound: servicesPerPax.outboundServices
          ? this.parseServices(servicesPerPax.outboundServices, servicesPerPax.outboundSeats)
          : undefined,
        servicesInbound: servicesPerPax.inboundServices
          ? this.parseServices(servicesPerPax.inboundServices, servicesPerPax.inboundSeats)
          : undefined,
        passengerAvatar: { title: { firstLetter: servicesPerPax.firstName, secondLetter: servicesPerPax.surname } }
      };
    });
  }
}
